export const HttpMethods = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  DELETE: 'delete',
} as const;

export type HttpMethod = (typeof HttpMethods)[keyof typeof HttpMethods];

export interface HttpClientErrorShape {
  message: string;
  status?: number;
  statusText?: string;
  body?: unknown;
}

export class HttpClientError extends Error implements HttpClientErrorShape {
  public status?: number;
  public statusText?: string;
  public body?: unknown;

  constructor(message: string, status?: number, statusText?: string, body?: unknown) {
    super(message);
    this.name = 'HttpClientError';
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

export const isHttpClientError = (error: unknown): error is HttpClientError => error instanceof HttpClientError;

const DEFAULT_HEADERS = {
  Accept: 'application/json, text/plain, */*',
  'Content-Type': 'application/json;charset=utf-8',
};

const tryParseJsonString = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const parseResponseBody = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';

  if (response.status === 204) {
    return null;
  }

  if (contentType.includes('application/json') || contentType.includes('+json')) {
    return response.json();
  }

  const rawText = await response.text();
  if (!rawText) {
    return null;
  }

  return tryParseJsonString(rawText);
};

const isAllowedUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url, window.location.origin);
    // Only allow same-origin requests
    return urlObj.origin === window.location.origin;
  } catch {
    // Relative URLs are safe
    return url.startsWith('/') || url.startsWith('./') || !url.includes('://');
  }
};

const request = async <T>(url: string, method: HttpMethod, body?: unknown): Promise<T> => {
  if (!isAllowedUrl(url)) {
    throw new HttpClientError(`Invalid URL: ${url} is not allowed`);
  }
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  
  const init: RequestInit = {
    method: method.toUpperCase(),
    headers: DEFAULT_HEADERS,
    credentials: 'include',
    signal: controller.signal,
  };

  if (body !== undefined && (method === HttpMethods.POST || method === HttpMethods.PUT)) {
    init.body = JSON.stringify(body);
  }
  
  try {
    let response: Response;

    try {
      response = await fetch(url, init);
    } catch (error) {
      throw new HttpClientError(error instanceof Error ? error.message : 'Network request failed');
    }

    const responseBody = await parseResponseBody(response);

    if (!response.ok) {
      throw new HttpClientError(
        `HTTP request failed with status ${response.status}`,
        response.status,
        response.statusText,
        responseBody
      );
    }

    return responseBody as T;
  } finally {
    clearTimeout(timeoutId);
  }
};

export const httpClient = {
  get: <T>(url: string) => request<T>(url, HttpMethods.GET),
  post: <T>(url: string, body?: unknown) => request<T>(url, HttpMethods.POST, body),
  put: <T>(url: string, body?: unknown) => request<T>(url, HttpMethods.PUT, body),
  delete: <T>(url: string) => request<T>(url, HttpMethods.DELETE),
  all: <T>(requests: Array<Promise<T>>) => Promise.all(requests),
};
